import { defineStore } from 'pinia'
import { useBackendApiGet } from '@/composables/useBackendApi'
import { type RequestState } from '~/types/ContentTypes'
import { type AuthResponse } from '@/types/AuthTypes'

export interface Article {
    title: string
}

type PageIndexEntry = {
    menu?: string
    children?: Record<string, PageIndexEntry>
    [key: string]: any
}

const mergeMainMenuChildrenIntoIndex = (index: Record<string, PageIndexEntry>) => {
    const normalizedIndex = { ...index }

    const collectChildren = (entry: PageIndexEntry) => {
        if (!entry?.children) {
            return
        }

        if (entry.menu === 'main') {
            Object.assign(normalizedIndex, entry.children)
        }

        for (const child of Object.values(entry.children)) {
            collectChildren(child)
        }
    }

    for (const entry of Object.values(index)) {
        collectChildren(entry)
    }

    return normalizedIndex
}

const normalizePageIndex = (index: Record<string, any>) => {
    if (!index?.global_index || typeof index.global_index !== 'object') {
        return index
    }

    return {
        ...index,
        global_index: mergeMainMenuChildrenIntoIndex(index.global_index)
    }
}

export const useRequestStore = defineStore('requests', {
    state: (): RequestState => ({
        pageID: 0,
        lang: 'de',
        darkmode: false,
        loggedIn: false,
        form: {},
        functionalCookies: false,
        showPreviewLayout: true,
        pageMeta: {} as object | {},
        pageContent: {},
        filterCategories: [] as Article[],
        pageIndex: {} as object | {},
        globals: {} as object | {}
    }),
    actions: {
        setPageID (id: number) {
            this.pageID = id
        },

        setLanguage (lang: string) {
            this.lang = lang
        },

        addPageContent (pageID: number, content: any) {
            this.pageContent[pageID] = content
        },

        addPageMeta (payload: any) {
            this.pageMeta = payload
        },

        async fillPageIndex (lang: string) {
            try {
                const index = await useBackendApiGet<AuthResponse>(`v1/page-index/${lang}?base=ws-website&article=article-page&service=service-menu`);

                if (index) {
                    this.pageIndex = normalizePageIndex(index)
                }
            } catch (error) {
                console.error(error)
            }
        },

        async fillCategories (lang: string) {
            try {
                const cats = await useBackendApiGet<AuthResponse>(`v1/categories/${lang}`);
        
                if (cats) {
                    this.filterCategories = cats
                }
            } catch (error) {
                console.error(error)
            }
        },

        async fillGlobals (lang: string) {
            try {
                const globals = await useBackendApiGet<AuthResponse>(`v1/globals/${lang}`);
    
                if (globals) {
                    this.globals = globals
                }
            } catch (error) {
                console.error(error)
            }
        },

        fillPageform (payload: any) {
            this.form = payload
        },

        toggleDarkmode (payload: boolean) {
            this.darkmode = payload
        },
        
        setFunctionalCookies (payload: boolean) {
            this.functionalCookies = payload

            // if (payload === true) {
            //     const iframe = document.querySelector('#wallpaper-iframe') as HTMLIFrameElement

            //     if (iframe && iframe.contentWindow) {
            //         const message = {
            //             type: 'value',
            //             payload: {
            //                 'functionalCookies': true
            //             }
            //         }

            //         iframe.contentWindow.postMessage(message, '*')
            //     }
            // }
        },

        setLoginState (payload: boolean) {
            this.loggedIn = payload
        },

        togglePreviewMarker () {
            this.showPreviewLayout = !this.showPreviewLayout
        }
    }
});